<template>
  <div
    class="bg-[#2F2D45] rounded-lg relative overflow-hidden w-full sm:min-w-[18.75rem] max-w-[min(25rem,_85vw)] sm:w-full shrink-0 flex flex-col"
  >
    <div class="flex items-center justify-between font-heading font-medium bg-[#3E3C5C] h-14 px-4">
      <div class="flex flex-1 shrink-0 items-center">
        <div v-if="bill.number">
          <span v-if="bill.customerCompany">{{ t('checkout.invoice') }}</span>
          <span v-else>{{ t('checkout.bill') }}</span>
          {{ bill.number }}
        </div>
        <div v-else-if="bill.id === pendingBillId">
          {{ t('checkout.pending-bill') }}
        </div>
        <div v-else-if="pendingBillId">{{ index }} / {{ totalBills - 1 }}</div>
        <div v-else>{{ index + 1 }} / {{ totalBills }}</div>
      </div>
      <div class="font-heading font-medium">
        {{ currency(bill.total) }}
      </div>

      <l-dropdown :options="options" :mark-selected="false" placement="bottom-end" trigger="click">
        <div
          v-if="bill.id !== pendingBillId"
          class="h-9 w-9 bg-[#5D5C8C] rounded flex items-center justify-center ml-2"
        >
          <l-icon name="3-dots" />
        </div>
      </l-dropdown>
    </div>

    <div class="flex-1 px-2 py-4 flex flex-col gap-2 min-h-0 overflow-y-auto">
      <bill-product v-for="product in bill.products" :key="product.id" :product="product" />
    </div>

    <div v-if="bill.customerCompany" class="rounded bg-[#1E1B2B] px-4 py-3 m-2">
      <div class="font-medium">{{ bill.customerCompany.name }}</div>
      <div class="font-normal text-n-200">
        {{ bill.customerCompany.taxId }} · {{ bill.customerCompany.address }}
      </div>
    </div>

    <div class="bg-[#3E3C5C] p-3 flex flex-col gap-3">
      <div v-if="bill.discountTotal > 0" class="flex justify-between text-sm text-n-200">
        <div class="flex gap-1">
          <div>{{ bill.discount.concept || t('tabs.discount') }}</div>
          <div v-if="bill.discount.type === 'percentage'">({{ bill.discount.amount }}%)</div>
        </div>
        <div>-{{ currency(bill.discountTotal) }}</div>
      </div>
      <div>
        <div class="font-bold">
          {{ t('checkout.payments') }}
        </div>
        <payments :bill="bill" />
      </div>

      <l-button
        v-if="bill.pending > 0"
        class="w-full"
        color="green"
        icon="cash"
        type="primary"
        @click="$emit('billSelected')"
      >
        {{ t('checkout.pay-amount', { amount: currency(bill.pending) }) }}
      </l-button>

      <div class="flex gap-2">
        <l-button
          class="shrink-0"
          icon="printer"
          size="medium"
          type="primary"
          @click="emit('print')"
        />
        <l-button
          v-if="!props.bill.customerCompany"
          class="w-full"
          icon="ticket"
          size="medium"
          type="primary"
          @click="$emit('generateInvoice')"
        >
          {{ t('checkout.generate-invoice') }}
        </l-button>
        <l-button
          v-if="props.bill.customerCompany"
          class="w-full"
          icon="mail-close"
          size="medium"
          type="primary"
          @click="emit('sendInvoice')"
        >
          {{ t('checkout.send-invoice') }}
        </l-button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { LButton, LDropdown, LIcon } from '@last/core-ui/paprika'
import type { Option } from '@last/core-ui/paprika/components/dropdown/types'

import { useMoney } from '@/composables/useMoney'
import type { BillWithPayments } from '@/types'

import BillProduct from './BillProduct.vue'
import Payments from './Payments.vue'

type Props = {
  bill: BillWithPayments
  pendingBillId?: string
  index: number
  totalBills: number
}

const props = withDefaults(defineProps<Props>(), {
  pendingBillId: '',
  index: 0,
  totalBills: 0
})

const emit = defineEmits<{
  billSelected: []
  invalidate: []
  generateInvoice: []
  cancelInvoice: []
  sendInvoice: []
  print: []
}>()

const { t } = useI18n()
const { currency } = useMoney()

const options = computed(() => {
  const options: Option[] = [
    {
      label: t('checkout.delete'),
      value: 'invalidate',
      icon: 'delete',
      onClick: () => emit('invalidate')
    }
  ]
  if (props.bill.customerCompany) {
    options.push({
      label: t('checkout.cancel-invoice'),
      value: 'cancelInvoice',
      icon: 'close',
      onClick: () => emit('cancelInvoice')
    })
  }

  return options
})
</script>
